import { base44 } from '@/api/base44Client';
import { getMemberByUser } from '@/lib/memberLookup';

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const normalizeText = (value) => String(value || '').trim().toLowerCase();
const normalizeName = (value) =>
  normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

const getMemberName = (member) => member?.full_name || member?.name || member?.member_name || '';

export function isSameTeamMember(left = {}, right = {}) {
  const leftIds = [
    left.id,
    left.member_id,
    left.team_member_id
  ].filter(Boolean).map(String);
  const rightIds = [
    right.id,
    right.member_id,
    right.team_member_id
  ].filter(Boolean).map(String);

  if (leftIds.some((id) => rightIds.includes(id))) return true;

  const leftEmails = [
    left.email,
    left.member_email
  ].map(normalizeEmail).filter(Boolean);
  const rightEmails = [
    right.email,
    right.member_email
  ].map(normalizeEmail).filter(Boolean);

  if (leftEmails.some((email) => rightEmails.includes(email))) return true;

  const leftNames = [
    left.name,
    left.full_name,
    left.member_name
  ].map(normalizeName).filter(Boolean);
  const rightNames = [
    right.name,
    right.full_name,
    right.member_name
  ].map(normalizeName).filter(Boolean);

  return leftNames.some((name) => rightNames.includes(name));
}

function buildIdentityCandidates(user, churchMember) {
  return {
    memberIds: new Set([churchMember?.id, churchMember?.member_id].filter(Boolean).map(String)),
    emails: new Set([user?.email, churchMember?.email, churchMember?.member_email].map(normalizeEmail).filter(Boolean)),
    names: new Set([user?.full_name, user?.name, churchMember?.full_name, churchMember?.name].map(normalizeName).filter(Boolean))
  };
}

function matchesIdentityCandidates(row, candidates) {
  if (!row) return false;
  if (row.member_id && candidates.memberIds.has(String(row.member_id))) return true;

  const rowEmail = normalizeEmail(row.email || row.member_email);
  if (rowEmail && candidates.emails.has(rowEmail)) return true;

  const rowName = normalizeName(getMemberName(row));
  return !!rowName && candidates.names.has(rowName);
}

async function getTeamMemberByEntity(user, entityName) {
  if (!user) return null;

  const churchMember = await getMemberByUser(user);
  if (churchMember?.id) {
    const byMemberId = await base44.entities[entityName].filter({ member_id: churchMember.id });
    if (byMemberId?.length) return byMemberId[0];
  }

  const userEmail = normalizeEmail(user.email);
  if (!userEmail) return null;

  const byEmail = await base44.entities[entityName].filter({ email: userEmail });
  if (byEmail?.length) return byEmail[0];

  const rows = await base44.entities[entityName].list('-created_date', 2000);
  const candidates = buildIdentityCandidates(user, churchMember);
  return rows.find((row) => matchesIdentityCandidates(row, candidates)) || null;
}

export const getWorshipTeamMemberByUser = (user) => getTeamMemberByEntity(user, 'WorshipTeamMember');
export const getMediaTeamMemberByUser = (user) => getTeamMemberByEntity(user, 'MediaTeamMember');
export const getDanceTeamMemberByUser = (user) => getTeamMemberByEntity(user, 'DanceTeamMember');
export const getReceptionTeamMemberByUser = (user) => getTeamMemberByEntity(user, 'ReceptionTeamMember');
export const getKidsTeamMemberByUser = (user) => getTeamMemberByEntity(user, 'KidsMBVMember');
